import { FC, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { Card, FancyLink, Loader, Message } from "shared/components";
import { Mail } from "shared/assets/icons";
import { Spacer } from "shared/layout";
import { copy } from "shared/assets";
import { getUserCardActions, useFetchingContactDetails } from "Bands/utils";
import { BAND_PATHS } from "Bands/router";
import { useFullUserProfileContext } from "Bands/contexts";
import { IStatus } from "shared/types";

import { Addresses, PhoneNumbers, EmergencyContacts, HomeBases } from ".";

const {
  profile: { profileIds, contactDetails },
} = copy;

interface Props {
  canManageProfile?: boolean;
}

export const ContactDetails: FC<Props> = ({ canManageProfile }) => {
  const history = useHistory();

  const { addresses, phoneNumbers, emergencyContacts, homeBases } =
    useFullUserProfileContext();

  const { fetchingContactDetails } = useFetchingContactDetails();

  const handleEdit = useCallback(() => {
    history.push(BAND_PATHS.EDIT_PROFILE_CONTACT);
  }, [history]);

  const actions = useMemo(
    () =>
      canManageProfile
        ? getUserCardActions({
            handleEdit,
          })
        : [],
    [canManageProfile, handleEdit]
  );

  const content = useMemo(() => {
    if (fetchingContactDetails) return <Loader />;
    if (
      !addresses?.length &&
      !phoneNumbers?.length &&
      !emergencyContacts?.length &&
      !homeBases?.length
    ) {
      return (
        <Message status={IStatus.warning}>
          {canManageProfile ? (
            <FancyLink
              to={BAND_PATHS.EDIT_PROFILE_CONTACT}
              status={IStatus.warning}
            >
              {contactDetails.empty}
            </FancyLink>
          ) : (
            contactDetails.emptyView
          )}
        </Message>
      );
    }

    return (
      <Spacer size="l">
        <Addresses />
        <PhoneNumbers />
        <EmergencyContacts />
        <HomeBases />
      </Spacer>
    );
  }, [
    fetchingContactDetails,
    addresses?.length,
    phoneNumbers?.length,
    emergencyContacts?.length,
    homeBases?.length,
    canManageProfile,
  ]);

  return (
    <Card
      id={profileIds.contact}
      title={contactDetails.title}
      icon={Mail}
      actions={actions}
      actionDataTestId="contact_details_actions"
    >
      {content}
    </Card>
  );
};
